import { useMemo } from 'react';

import { Menu, Divider, Typography, ListItem } from '@mui/material';
import { v4 } from 'uuid';

import {
    Geometry,
    HiddenStates,
    MarkerData,
    StateData,
    StateGroupData,
} from 'types/map';

import { ColorPicker } from '../controls/ColorPicker';
import { GroupCreator } from '../controls/GroupCreator';
import { GroupRemover } from '../controls/GroupRemover';
import { HiddenMultiple } from '../controls/HiddenMultiple';
import { HiddenSingle } from '../controls/HiddenSingle';
import { LinkSelector } from '../controls/LinkSelector';
import { MarkerCreator } from '../controls/MarkerCreator';
import { NameEditor } from '../controls/NameEditor';
import { StateAllSelector } from '../controls/StateAllSelector';
import { StateSelector } from '../controls/StateSelector';

type StateCtxMenuProps = {
    onClose?: () => void;
    menuAnchor?: {
        top: number;
        left: number;
    };
    states?: StateData[];
    setStates?: (states: StateData[]) => void;
    groups?: StateGroupData[];
    setGroups?: (groups: StateGroupData[]) => void;
    hidden?: HiddenStates;
    setHidden?: (hidden: HiddenStates) => void;
    selected?: number[];
    setSelected?: (selected: number[]) => void;
    markers?: MarkerData[];
    setMarkers?: (markers: MarkerData[]) => void;
    currentGeoID?: number;
    geometries?: Geometry[];
};

export const StateCtxMenu = ({
    onClose: handleCtxMenuClose = () => undefined,
    menuAnchor,
    setStates = () => undefined,
    states = [],
    setGroups = () => undefined,
    groups = [],
    setHidden = () => undefined,
    hidden = [],
    setSelected = () => undefined,
    selected = [],
    setMarkers = () => undefined,
    markers = [],
    currentGeoID,
    geometries = [],
}: StateCtxMenuProps): JSX.Element => {
    const geoProps = useMemo(() => {
        const props = geometries.find(
            (el) => el.properties.id === currentGeoID
        )?.properties;

        return props
            ? {
                  ...props,
                  hidden: hidden.includes(props.id),
                  selected: selected.includes(props.id),
              }
            : undefined;
    }, [currentGeoID, hidden, geometries, selected]);

    const handleStateDataChange = (data: Partial<StateData>): void => {
        if (currentGeoID) {
            const currentState = states.find((el) => el.id === currentGeoID);

            if (currentState) {
                setStates([
                    ...states.filter((el) => el.id !== currentState.id),
                    {
                        ...currentState,
                        ...data,
                    },
                ]);
            } else {
                setStates([
                    ...states,
                    {
                        id: currentGeoID,
                        ...data,
                    },
                ]);
            }
        }
    };
    const handleGroupDataChange = (data: Partial<StateGroupData>): void => {
        if (geoProps?.id && geoProps?.group) {
            const currentGroup = groups.find((el) =>
                el.states.includes(geoProps.id)
            );

            if (currentGroup) {
                setGroups([
                    ...groups.filter((el) => el.id !== currentGroup.id),
                    {
                        ...currentGroup,
                        ...data,
                    },
                ]);
            }
        }
    };

    const toggleHidden = (): void => {
        if (geoProps?.hidden) {
            setHidden(hidden.filter((el) => el !== geoProps.id));
        } else if (geoProps?.id) {
            setHidden([...hidden, geoProps.id]);
        }
    };
    const toggleSelected = (id: number): void => {
        if (selected.includes(id)) {
            setSelected(selected.filter((el) => el !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    const selectAll = (): void => {
        setSelected(geometries.map((el) => el.properties.id));
    };

    const createGroup = (): void => {
        const idx = v4();

        setGroups([
            ...groups,
            {
                id: idx,
                states: selected,
                name: `Группа #${idx.slice(0, 4)}`,
            },
        ]);
    };

    const removeGroup = (): void => {
        if (geoProps?.id) {
            setGroups(groups.filter((el) => !el.states.includes(geoProps.id)));
        }
    };

    const clickedOnSelected = useMemo(
        () =>
            currentGeoID
                ? selected.includes(currentGeoID) && selected.length > 1
                : false,
        [currentGeoID, selected]
    );

    const selectedIncludesHidden = useMemo(
        () => selected.some((el) => hidden.includes(el)),
        [hidden, selected]
    );
    const selectedIncludesGroup = useMemo(
        () => selected.some((el) => groups.some((g) => g.states.includes(el))),
        [groups, selected]
    );

    return (
        <Menu
            open={Boolean(menuAnchor)}
            onClose={handleCtxMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={menuAnchor}
        >
            <ListItem
                sx={{
                    py: 0.5,
                    maxWidth: 220,
                    display: !geoProps?.name ? 'none' : undefined,
                }}
            >
                <Typography noWrap>{geoProps?.name}</Typography>
            </ListItem>
            <Divider
                sx={{
                    mb: 0.5,
                    display: !geoProps?.name ? 'none' : undefined,
                }}
            />
            <StateSelector
                selected={geoProps?.selected}
                onClick={() => {
                    handleCtxMenuClose();
                    if (geoProps?.id) {
                        toggleSelected(geoProps.id);
                    }
                }}
            />
            <StateAllSelector
                selectedAll={selected.length === geometries.length}
                onClick={() => {
                    handleCtxMenuClose();
                    selectAll();
                }}
            />
            <Divider sx={{ my: 0.5 }} />
            {!clickedOnSelected ? (
                <HiddenSingle
                    disabled={!!geoProps?.group}
                    hidden={geoProps?.hidden}
                    onClick={() => {
                        handleCtxMenuClose();
                        toggleHidden();
                    }}
                />
            ) : (
                <HiddenMultiple
                    hideDisabled={selectedIncludesGroup}
                    hidden={hidden}
                    selected={selected}
                    onHide={() => {
                        setHidden(
                            Array.from(new Set([...hidden, ...selected]))
                        );
                        setSelected([]);
                        handleCtxMenuClose();
                    }}
                    onShow={() => {
                        setHidden(
                            Array.from(
                                new Set([
                                    ...hidden.filter(
                                        (el) => !selected.includes(el)
                                    ),
                                ])
                            )
                        );
                        setSelected([]);
                        handleCtxMenuClose();
                    }}
                />
            )}
            <Divider sx={{ my: 0.5 }} />
            {!clickedOnSelected ? (
                <ColorPicker
                    onClose={handleCtxMenuClose}
                    onColorChange={(color) =>
                        geoProps?.group
                            ? handleGroupDataChange({ color })
                            : handleStateDataChange({ color })
                    }
                    color={geoProps?.color}
                    disabled={geoProps?.hidden}
                />
            ) : undefined}
            {!clickedOnSelected ? (
                <NameEditor
                    onClose={handleCtxMenuClose}
                    onChange={(name) =>
                        geoProps?.group
                            ? handleGroupDataChange({ name })
                            : handleStateDataChange({ name })
                    }
                    name={geoProps?.name || ''}
                    disabled={geoProps?.hidden}
                />
            ) : undefined}
            {!clickedOnSelected ? (
                <LinkSelector
                    onClose={handleCtxMenuClose}
                    onChange={(hrefUuid) =>
                        geoProps?.group
                            ? handleGroupDataChange({ hrefUuid })
                            : handleStateDataChange({ hrefUuid })
                    }
                    value={geoProps?.hrefUuid}
                    disabled={geoProps?.hidden}
                />
            ) : undefined}
            {!clickedOnSelected ? (
                <MarkerCreator
                    onClose={handleCtxMenuClose}
                    onCreate={(coords) => {
                        setMarkers([
                            ...markers,
                            {
                                id: v4(),
                                coords,
                                name: 'Маркер',
                                color: '#ff5722',
                            },
                        ]);
                        handleCtxMenuClose();
                    }}
                    disabled={geoProps?.hidden}
                />
            ) : undefined}
            {clickedOnSelected ? (
                <GroupCreator
                    onClick={() => {
                        createGroup();
                        setSelected([]);
                        handleCtxMenuClose();
                    }}
                    disabled={selectedIncludesHidden || selectedIncludesGroup}
                />
            ) : undefined}
            {geoProps?.group && !clickedOnSelected ? (
                <GroupRemover
                    onClick={() => {
                        removeGroup();
                        setSelected([]);
                        handleCtxMenuClose();
                    }}
                />
            ) : undefined}
        </Menu>
    );
};
